import { haveSameTags } from "./bulkTags"

interface LedgerEntry {
  /** Writes for the record that have not settled yet. */
  inFlightCount: number
  /** Tags the newest write that landed set, for a failure to fall back to. */
  landedTags?: string[]
  /** Tags the record's newest write set, or is trying to set. */
  tags: string[]
  /** The newest write, so a failure cannot roll a later write back. */
  writeId: number
}

interface TagWrite {
  /**
   * Records how the write ended, rolling the record back if it failed.
   *
   * Reports whether the write is still the record's newest. A later edit that
   * was built on what this write was setting owns the record instead, and has
   * absorbed this one's outcome — there is nothing left for its caller to roll
   * back or report.
   */
  settle: (landed: boolean) => boolean
}

/**
 * The tags each record's newest tag write set, keyed by resource and record id.
 *
 * A record's rows lag its writes: a write only shows up once the list refetches,
 * and one that moves a record out of the active filter drops its row entirely.
 * So a whole tag list built from a row alone can predate an edit that already
 * landed, and writing it back silently drops the tags that edit added.
 *
 * Module scope for the same reason the write chains are: the controls that
 * write tags come and go while their writes are outstanding, and a record can
 * leave a selection and rejoin it before the list has caught up. A baseline
 * owned by one of those controls is gone by the time the next one needs it.
 *
 * Ids are only unique within their own resource, so `resource` — a fixed name
 * like `"agents"`, never an id — joins the id to make the key.
 */
const entriesByRecord = new Map<string, LedgerEntry>()

/**
 * How many live readers are holding each record, so overflow eviction can skip
 * the records something on screen is still reading.
 */
const retainCountByRecord = new Map<string, number>()

const listeners = new Set<() => void>()

let lastWriteId = 0
let version = 0
let snapshot: ReadonlyMap<string, string[]> = new Map()
let snapshotVersion = -1

/**
 * An upper bound on remembered records.
 *
 * Entries are normally dropped as soon as a row confirms them, but a selection
 * is often cleared the moment a bulk edit is applied, which unmounts the only
 * reader that would have seen those rows arrive. Rather than remember every
 * record edited in a session, the oldest abandoned entries are dropped once the
 * ledger grows past what a plausible run of edits needs.
 *
 * Only abandoned entries are droppable: a record a control is still reading is
 * kept however far the ledger has grown, since dropping one would send that
 * control back to a row that has yet to carry the write.
 */
const MAX_TRACKED_RECORDS = 500

/** The key a record's tags are stored under, for reading a snapshot. */
export function tagWriteKey(resource: string, recordId: string): string {
  return `${resource}/${recordId}`
}

/**
 * Marks a write as started and reports the tags it is setting as the record's.
 *
 * The tags count from the moment the write is queued rather than from when it
 * lands, so an edit that follows it diffs against what the record is becoming
 * instead of against the list the write is already replacing.
 */
export function beginTagWrite(
  resource: string,
  recordId: string,
  tags: string[],
): TagWrite {
  const key = tagWriteKey(resource, recordId)
  const previousEntry = entriesByRecord.get(key)

  lastWriteId += 1
  const writeId = lastWriteId

  setEntry(key, {
    inFlightCount: (previousEntry?.inFlightCount ?? 0) + 1,
    landedTags: previousEntry?.landedTags,
    tags,
    writeId,
  })

  return {
    settle: (landed: boolean) => settleTagWrite(key, writeId, tags, landed),
  }
}

/** The tags a record's newest write set, if the rows have yet to carry them. */
export function readTagWrite(
  resource: string,
  recordId: string,
): string[] | undefined {
  return entriesByRecord.get(tagWriteKey(resource, recordId))?.tags
}

/**
 * Holds the given records for as long as the returned release is uncalled, so
 * overflow eviction leaves them alone.
 *
 * Eviction is otherwise blind to who is reading: it drops the entries it has
 * held longest, which is fine for a record nothing is looking at and wrong for
 * one that is selected or on screen, whose next edit would then be built from
 * a row that predates the write the entry is remembering.
 */
export function retainTagWrites(
  resource: string,
  recordIds: readonly string[],
): () => void {
  const keys = recordIds.map((recordId) => tagWriteKey(resource, recordId))

  for (const key of keys) {
    retainCountByRecord.set(key, (retainCountByRecord.get(key) ?? 0) + 1)
  }

  return () => {
    for (const key of keys) {
      const retainCount = (retainCountByRecord.get(key) ?? 0) - 1
      if (retainCount > 0) {
        retainCountByRecord.set(key, retainCount)
      } else {
        retainCountByRecord.delete(key)
      }
    }
  }
}

/**
 * Drops a record whose row now carries what its writes left, so the rows are
 * the authority again for every record the ledger is not ahead of.
 */
export function forgetSettledTagWrite(
  resource: string,
  recordId: string,
  rowTags: readonly string[],
): void {
  const key = tagWriteKey(resource, recordId)
  const entry = entriesByRecord.get(key)

  if (!entry || entry.inFlightCount > 0 || !haveSameTags(entry.tags, rowTags)) {
    return
  }

  entriesByRecord.delete(key)
  publish()
}

export function subscribeToTagWrites(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Every remembered record's tags, as a value that changes only on a write. */
export function getTagWriteSnapshot(): ReadonlyMap<string, string[]> {
  if (snapshotVersion !== version) {
    snapshot = new Map(
      Array.from(entriesByRecord, ([key, entry]) => [key, entry.tags]),
    )
    snapshotVersion = version
  }

  return snapshot
}

function settleTagWrite(
  key: string,
  writeId: number,
  tags: string[],
  landed: boolean,
): boolean {
  const entry = entriesByRecord.get(key)
  if (!entry) {
    return false
  }

  const inFlightCount = Math.max(entry.inFlightCount - 1, 0)
  const isNewestWrite = entry.writeId === writeId

  // Writes for a record are queued behind each other, so the one settling here
  // is the newest that has reached the service either way.
  if (landed) {
    setEntry(key, { ...entry, inFlightCount, landedTags: tags })
    return isNewestWrite
  }

  // A later edit already owns the record. It was built on the tags this write
  // was setting, so rolling those back now would undo the newer edit instead.
  if (!isNewestWrite) {
    setEntry(key, { ...entry, inFlightCount })
    return false
  }

  // Nothing this record wrote has landed, so its row is the authority again.
  if (!entry.landedTags) {
    entriesByRecord.delete(key)
    publish()
    return true
  }

  setEntry(key, { ...entry, inFlightCount, tags: entry.landedTags })
  return true
}

function setEntry(key: string, entry: LedgerEntry): void {
  entriesByRecord.set(key, entry)
  evictSettledOverflow(key)
  publish()
}

function evictSettledOverflow(keptKey: string): void {
  if (entriesByRecord.size <= MAX_TRACKED_RECORDS) {
    return
  }

  for (const [key, entry] of entriesByRecord) {
    if (entriesByRecord.size <= MAX_TRACKED_RECORDS) {
      return
    }

    if (
      key !== keptKey &&
      entry.inFlightCount === 0 &&
      !retainCountByRecord.has(key)
    ) {
      entriesByRecord.delete(key)
    }
  }
}

function publish(): void {
  version += 1
  for (const listener of listeners) {
    listener()
  }
}
